/**
 * Web 发版版本检测（方案 A：public/version.json）
 * - 构建时 build 序号自动 +1
 * - 启动 / 回前台 / Chunk 加载失败时对比远端 version.json
 */
import { Modal } from 'ant-design-vue'

const STORAGE_KEY = '__cibeiai_web_build_version__'
const CHUNK_RELOAD_SESSION_KEY = '__cibeiai_chunk_reload_once__'

function isDev () {
  return process.env.NODE_ENV === 'development'
}

/** 当前 JS 包内嵌版本（构建时注入） */
export function getEmbeddedVersion () {
  return process.env.VUE_APP_BUILD_VERSION || 'dev'
}

function resolveVersionUrl () {
  const base = process.env.BASE_URL || '/'
  const normalizedBase = base.endsWith('/') ? base : `${base}/`
  return `${normalizedBase}version.json`
}

/** 拉取线上 version.json（禁止缓存） */
export async function fetchRemoteVersion () {
  const url = `${resolveVersionUrl()}?t=${Date.now()}`
  const res = await fetch(url, { cache: 'no-store' })
  if (!res.ok) {
    throw new Error(`version.json HTTP ${res.status}`)
  }
  const data = await res.json()
  return String(data.version || data.build || '').trim()
}

export function getStoredVersion () {
  try {
    return localStorage.getItem(STORAGE_KEY) || ''
  } catch {
    return ''
  }
}

export function storeVersion (version) {
  if (!version) return
  try {
    localStorage.setItem(STORAGE_KEY, version)
  } catch {
    // ignore
  }
}

/** 带版本号强制刷新，绕过 index.html 缓存 */
export function reloadWithVersion (version, reason = 'update') {
  const url = new URL(window.location.href)
  url.searchParams.set('v', version)
  url.searchParams.set('_r', reason)
  window.location.replace(url.toString())
}

function getUrlVersionParam () {
  try {
    return new URLSearchParams(window.location.search).get('v') || ''
  } catch {
    return ''
  }
}

function markChunkReloadAttempted () {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1')
  } catch {
    // ignore
  }
}

function hasChunkReloadAttempted () {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * 启动前静默校验：本地包版本与 version.json 不一致则立即刷新
 * @returns {Promise<boolean>} true 表示已触发刷新（调用方应中止后续启动）
 */
export async function ensureLatestVersionBeforeBoot () {
  if (isDev()) return false

  const embedded = getEmbeddedVersion()
  if (!embedded || embedded === 'dev') return false

  let remote = ''
  try {
    remote = await fetchRemoteVersion()
  } catch (e) {
    console.warn('[appVersion] 启动版本校验跳过:', e)
    storeVersion(embedded)
    return false
  }

  if (!remote || remote === embedded) {
    storeVersion(embedded)
    return false
  }

  // 已带目标版本参数仍不一致，避免死循环，放行由 Chunk 兜底
  if (getUrlVersionParam() === remote) {
    storeVersion(embedded)
    return false
  }

  reloadWithVersion(remote, 'startup')
  return true
}

let visibilityCheckBound = false
let versionPromptOpen = false

function promptReloadForNewVersion (remoteVersion) {
  if (versionPromptOpen) return
  versionPromptOpen = true

  Modal.confirm({
    title: '发现新版本',
    content: '站点已更新，请刷新页面以继续使用最新功能。',
    okText: '立即刷新',
    cancelText: '稍后',
    centered: true,
    onOk: () => {
      reloadWithVersion(remoteVersion, 'prompt')
    },
    onCancel: () => {
      versionPromptOpen = false
    }
  })
}

/** 挂载后：切回前台时检测新版本 */
export async function checkForNewVersionOnVisible () {
  if (isDev() || document.visibilityState !== 'visible') return

  const embedded = getEmbeddedVersion()
  if (!embedded || embedded === 'dev') return

  let remote = ''
  try {
    remote = await fetchRemoteVersion()
  } catch {
    return
  }

  if (remote && remote !== embedded) {
    promptReloadForNewVersion(remote)
  }
}

export function initAppVersionWatch () {
  if (isDev() || visibilityCheckBound) return
  visibilityCheckBound = true

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForNewVersionOnVisible()
    }
  })
}

/**
 * 路由懒加载 Chunk 失败时：拉最新 version.json 并带版本号刷新（同 session 仅一次）
 */
export async function recoverFromChunkLoadError (error) {
  if (isDev()) {
    window.location.reload()
    return
  }

  if (hasChunkReloadAttempted()) {
    Modal.error({
      title: '资源加载失败',
      content: '页面资源可能已过期，请手动刷新浏览器；若仍失败，请清除缓存或重新添加主屏幕快捷方式。',
      okText: '刷新页面',
      centered: true,
      onOk: () => window.location.reload()
    })
    console.error('[appVersion] ChunkLoadError after retry:', error)
    return
  }

  markChunkReloadAttempted()

  let version = ''
  try {
    version = await fetchRemoteVersion()
  } catch {
    version = String(Date.now())
  }

  reloadWithVersion(version || String(Date.now()), 'chunk')
}
